/**
 * A tela de Execuções (spec 096, US3).
 *
 * **POR QUE ELA É DA PLATAFORMA E NÃO DO MÓDULO.** Execução existe para qualquer
 * agente, de qualquer projeto — com ou sem atendimento. Por isso a listagem é da
 * ORGANIZAÇÃO inteira (`GET /organizations/{id}/executions`), e não de um agente
 * escolhido antes.
 *
 * **SOBRE O IDIOMA.** Os textos aqui são pt-BR direto, como os passos do
 * assistente de agente (`WIZARD_STEPS`). O módulo que esta tela serve é de
 * escritório brasileiro e o vocabulário do documento do tenant é pt-BR por
 * contrato; traduzir a casca sem traduzir o conteúdo entregaria meia tela.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Activity, AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { EscopoDaOrganizacao } from '@/components/layout/escopo-da-organizacao'
import { FaixaDePeriodo } from '@/components/painel'
import { dolarMiudo, inteiro } from '@/lib/formato'
import { useOrganizationStore } from '@/stores/organization-store'
import { desempenhoService, execucoesService } from '@/services/painel'
import {
  estadoDaExecucao,
  minutosSemSinal,
  TETO_DA_FILA_MIN,
  type AskStatus,
  type AttendanceGroup,
  type AttendanceSummaryView,
  type EstadoDaExecucao,
  type OrganizationExecution,
} from '@/schemas/painel'
import { cn } from '@/lib/utils'

const RECARGA_MS = 5000

/** O período, na mesma língua do resto do painel. */
const PERIODOS = [
  { valor: 7, rotulo: '7 dias' },
  { valor: 14, rotulo: '14 dias' },
  { valor: 30, rotulo: '30 dias' },
  { valor: 90, rotulo: '90 dias' },
] as const

/** Quantas linhas por página. */
const POR_PAGINA = 50
// O LOTE DAS RUNNING para o aviso de parada: o teto da rota.
const LOTE_RODANDO = 200

/**
 * As execuções `running` MAIS ANTIGAS, para o aviso "parada?". A rota devolve
 * da mais nova para a mais antiga, e a parada é justamente a antiga: com mais
 * running que o lote, o primeiro lote perderia as paradas. Nesse caso lê o fim
 * da lista, que são as mais antigas.
 */
async function rodandoMaisAntigas(organizacaoId: string) {
  const primeiro = await execucoesService.listar(organizacaoId, {
    status: 'running',
    limit: LOTE_RODANDO,
  })
  if (primeiro.total <= primeiro.items.length) return primeiro.items
  const fim = await execucoesService.listar(organizacaoId, {
    status: 'running',
    limit: LOTE_RODANDO,
    offset: primeiro.total - LOTE_RODANDO,
  })
  return fim.items
}

/**
 * As abas, e por que "Em curso" quer dizer DUAS coisas diferentes.
 *
 * Na tabela de TURNOS ela é `status = 'running'`: existe uma execução sendo
 * processada neste instante.
 *
 * Na de CONVERSAS esse filtro é inútil, e o número que mostra o tamanho da
 * inutilidade é este: em 501 execuções de 30 dias em produção, **nenhuma foi
 * jamais observada como `running`**. Um turno dura de 2 a 21 segundos e a
 * tela recarrega a cada 5 — a janela praticamente nunca é pega. Uma aba que
 * nunca mostra nada não informa: engana, porque quem clica conclui que não
 * há conversa em curso.
 *
 * Numa lista de conversas o que corresponde à pergunta é a conversa ainda
 * aberta (`closed_at` nulo). O rótulo serve aos dois sentidos porque a
 * unidade está escrita ao lado, na própria tela.
 */
const FILTROS = [
  { valor: 'todas', rotulo: 'Todas', status: undefined },
  { valor: 'em_curso', rotulo: 'Em curso', status: 'running' },
  { valor: 'falhas', rotulo: 'Falhas', status: 'failed' },
] as const

const APARENCIA: Record<EstadoDaExecucao, { rotulo: string; classe: string }> = {
  em_curso: { rotulo: 'em curso', classe: 'bg-blue-500/15 text-blue-300' },
  parada: { rotulo: 'parada?', classe: 'bg-amber-500/15 text-amber-300' },
  concluida: { rotulo: 'concluída', classe: 'bg-emerald-500/15 text-emerald-300' },
  falhou: { rotulo: 'falhou', classe: 'bg-red-500/15 text-red-300' },
  cancelada: { rotulo: 'cancelada', classe: 'bg-gray-500/15 text-gray-400' },
  na_fila: { rotulo: 'na fila', classe: 'bg-indigo-500/15 text-indigo-300' },
}

function duracao(execucao: OrganizationExecution): string {
  const inicio = execucao.started_at ?? execucao.created_at
  const fim = execucao.completed_at ? new Date(execucao.completed_at) : new Date()
  const segundos = Math.max(
    0,
    Math.round((fim.getTime() - new Date(inicio).getTime()) / 1000)
  )
  const m = Math.floor(segundos / 60)
  const s = segundos % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function hora(instante: string | null): string {
  if (!instante) return '—'
  return new Date(instante).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** "hoje 15:43", "ontem 17:22", "21/07 14:50" — como o mockup escreve. */
function quando(instante: string): string {
  const d = new Date(instante)
  const hora = d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const hoje = new Date()
  const mesmoDia = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  if (mesmoDia(d, hoje)) return `hoje ${hora}`
  const ontem = new Date(hoje)
  ontem.setDate(hoje.getDate() - 1)
  if (mesmoDia(d, ontem)) return `ontem ${hora}`
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`
}

/**
 * A UNIDADE ESCOLHIDA PELA PESSOA, guardada por escritório.
 *
 * Por escritório porque a escolha faz sentidos diferentes: num que tem funil,
 * "conversas" é a leitura natural; num que não tem, a mesma preferência
 * levada junto esconderia execuções.
 *
 * A leitura é SÍNCRONA, no corpo do componente, e não num efeito — um efeito
 * roda depois da primeira pintura, que é exatamente o pisca que isto existe
 * para eliminar. O acesso vai dentro de `try` porque `localStorage` lança em
 * aba anônima e com dados de site bloqueados, e uma tela de leitura não pode
 * cair por causa de uma preferência.
 */
function chaveDaUnidade(organizacao: string | undefined): string {
  return `execucoes.contando.${organizacao ?? 'sem-organizacao'}`
}

function unidadeLembrada(
  organizacao: string | undefined
): 'turnos' | 'conversas' | null {
  try {
    const guardado = localStorage.getItem(chaveDaUnidade(organizacao))
    return guardado === 'turnos' || guardado === 'conversas' ? guardado : null
  } catch {
    return null
  }
}

function lembrarUnidade(
  organizacao: string | undefined,
  unidade: 'turnos' | 'conversas'
): void {
  try {
    localStorage.setItem(chaveDaUnidade(organizacao), unidade)
  } catch {
    // Preferência não guardada não é erro de leitura: a tela segue igual,
    // só não lembra na próxima visita.
  }
}

function dinheiro(valor: number | null): string {
  if (valor === null || valor === 0) return '—'
  return dolarMiudo(valor)
}

/** Um filtro aplicado, com o jeito de tirá-lo. */
function Chip({ rotulo, aoLimpar }: { rotulo: string; aoLimpar: () => void }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-md border border-indigo-500/35 bg-indigo-500/10 px-3 py-1 text-xs text-indigo-200">
      {rotulo}
      <button
        type="button"
        onClick={aoLimpar}
        aria-label={`Remover filtro ${rotulo}`}
        className="text-indigo-400 hover:text-indigo-200"
      >
        ×
      </button>
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* A LISTA AGRUPADA POR ATENDIMENTO (spec 128)                                 */
/*                                                                            */
/* Na unidade "conversas", uma linha por atendimento (sessão do canal e        */
/* ciclo). Aberta, a linha mostra o bloco do atendimento em DUAS regiões, e a  */
/* separação é a tese da tela: o que o sistema registrou ("Fatos do            */
/* atendimento") nunca se mistura com o que o modelo escreveu ("Resumo gerado  */
/* pela IA"). Direção do desenho no corpo do PR (o `shape` da lista agrupada). */
/* -------------------------------------------------------------------------- */

/** O estado do pedido em PALAVRA, nunca só em cor. */
const ESTADO_DO_PEDIDO: Record<AskStatus, { rotulo: string; classe: string }> = {
  fulfilled: { rotulo: 'atendido', classe: 'bg-emerald-500/15 text-emerald-300' },
  not_fulfilled: { rotulo: 'não atendido', classe: 'bg-red-500/15 text-red-300' },
  unanswered: { rotulo: 'sem resposta', classe: 'bg-gray-500/15 text-gray-300' },
}

/** Por que não há resumo, na língua de quem lê. */
const MOTIVO_SEM_RESUMO: Record<string, string> = {
  no_provider_key: 'sem chave do provedor configurada',
  budget_exceeded: 'teto diário do escritório atingido',
  model_error: 'o modelo não respondeu em duas tentativas',
  no_conversation: 'sem conversa para resumir',
  before_summary: 'conversa anterior ao resumo',
}

const PRIORIDADE: Record<string, string> = {
  low: 'baixa',
  medium: 'média',
  high: 'alta',
}

/** "1:35", ou "1 h 05 min" quando passa da hora. */
function duracaoDoAtendimento(ms: number | null): string {
  if (ms === null) return '—'
  const segundos = Math.max(0, Math.round(ms / 1000))
  const h = Math.floor(segundos / 3600)
  const m = Math.floor((segundos % 3600) / 60)
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`
  return `${m}:${String(segundos % 60).padStart(2, '0')}`
}

/** Com quem se falou: o cliente identificado, senão o contato. */
function quemFalou(grupo: AttendanceGroup): string | null {
  return grupo.client_name ?? grupo.contact.name ?? grupo.contact.phone ?? null
}

/** O desfecho da linha. "em curso" e o transbordo dizem o que são. */
function desfecho(grupo: AttendanceGroup): { rotulo: string; classe: string } {
  if (grupo.state === 'in_progress')
    return { rotulo: 'em curso', classe: 'bg-blue-500/15 text-blue-300' }
  if (grupo.state === 'handoff')
    return {
      rotulo: 'Transferido para humano',
      classe: 'bg-amber-500/15 text-amber-300',
    }
  return {
    rotulo: grupo.outcome ?? 'encerrada',
    classe: 'bg-gray-500/15 text-gray-300',
  }
}

/** Metadados da entrega: dado opaco do escritório, mostrado como chave e valor. */
function metadadosDaEntrega(metadata: Record<string, unknown>): string {
  return Object.entries(metadata)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' · ')
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <dt className="text-xs uppercase tracking-wide text-gray-500">{children}</dt>
  )
}

function FatosDoAtendimento({ grupo, id }: { grupo: AttendanceGroup; id: string }) {
  const fim = desfecho(grupo)
  return (
    <section aria-labelledby={id} className="min-w-0 space-y-4 break-words">
      <h3 id={id} className="text-sm font-semibold text-gray-200">
        Fatos do atendimento
      </h3>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        <div>
          <Rotulo>Desfecho</Rotulo>
          <dd className="mt-0.5 text-gray-200">{fim.rotulo}</dd>
        </div>
        <div>
          <Rotulo>Duração</Rotulo>
          <dd className="mt-0.5 tabular-nums text-gray-200">
            {duracaoDoAtendimento(grupo.duration_ms)}
          </dd>
        </div>
        <div>
          <Rotulo>Custo</Rotulo>
          <dd className="mt-0.5 tabular-nums text-gray-200">{dinheiro(grupo.cost)}</dd>
        </div>
      </dl>

      {grupo.handoff && (
        <div className="space-y-1 text-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500">Transbordo</p>
          <p className="text-gray-200">
            Motivo:{' '}
            <span translate="no" className="font-mono text-xs">
              {grupo.handoff.reason}
            </span>
            {grupo.handoff.queued && (
              <span className="ml-2 text-xs text-amber-300">na fila da equipe</span>
            )}
          </p>
          {grupo.handoff.customer_message && (
            <p className="text-gray-400">
              Mensagem dita ao cliente: “{grupo.handoff.customer_message}”
            </p>
          )}
        </div>
      )}

      <div className="space-y-1 text-sm">
        <p className="text-xs uppercase tracking-wide text-gray-500">Entregas</p>
        {grupo.deliveries.length === 0 ? (
          <p className="text-gray-500">Nenhuma entrega neste atendimento.</p>
        ) : (
          <ul className="space-y-1">
            {grupo.deliveries.map((entrega, i) => (
              <li key={`${entrega.delivered_at}-${i}`} className="text-gray-300">
                {entrega.name && <span className="text-gray-200">{entrega.name}</span>}
                {metadadosDaEntrega(entrega.metadata) && (
                  <span className={cn('text-gray-400', entrega.name && 'ml-2')}>
                    {metadadosDaEntrega(entrega.metadata)}
                  </span>
                )}
                <span className="ml-2 tabular-nums text-xs text-gray-500">
                  {quando(entrega.delivered_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-1 text-sm">
        <p className="text-xs uppercase tracking-wide text-gray-500">
          Turnos{' '}
          <span className="normal-case tracking-normal tabular-nums">
            ({contagemDeTurnos(grupo)})
          </span>
        </p>
        <ul className="divide-y divide-gray-800/50">
          {grupo.executions.map((turno) => (
            <li
              key={turno.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5"
            >
              <Link
                to={`/executions/${turno.id}`}
                className="inline-flex min-h-11 items-center text-indigo-300 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-400 sm:min-h-0"
              >
                {quando(turno.created_at)} · {turno.agent_name}
              </Link>
              <span className="tabular-nums text-xs text-gray-500">
                {dinheiro(turno.cost)}
              </span>
              {turno.status === 'failed' && (
                <span className="text-xs text-red-300">falhou</span>
              )}
              {turno.text_expired && (
                <span className="text-xs text-amber-500/80">texto expirado</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function ListaDeTexto({ titulo, itens }: { titulo: string; itens: string[] }) {
  if (itens.length === 0) return null
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-gray-500">{titulo}</p>
      <ul className="list-disc space-y-0.5 pl-5 text-gray-300">
        {itens.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

function CorpoDoResumo({ resumo }: { resumo: AttendanceSummaryView }) {
  if (resumo.status === 'pending')
    return <p className="text-gray-400">O resumo ainda não chegou</p>
  if (resumo.status === 'unavailable')
    return (
      <p className="text-gray-400">
        Resumo indisponível:{' '}
        {MOTIVO_SEM_RESUMO[resumo.reason ?? ''] ?? 'motivo não informado'}
      </p>
    )
  const categoria = resumo.suggested_category
  const prioridade = resumo.suggested_priority
  const sugestoes = (
    <p className="text-xs text-gray-400">
      Categoria sugerida: {categoria ?? 'sem categoria'} · Prioridade sugerida:{' '}
      {prioridade ? (PRIORIDADE[prioridade] ?? prioridade) : 'sem prioridade'}
    </p>
  )
  if (!resumo.prose) {
    return (
      <div className="space-y-2">
        <p className="text-gray-400">Texto do resumo apagado pela retenção</p>
        {resumo.ask_statuses.length > 0 && (
          <ol className="space-y-0.5 text-gray-300">
            {resumo.ask_statuses.map((estado, i) => (
              <li key={i}>
                Pedido {i + 1}: {ESTADO_DO_PEDIDO[estado].rotulo}
              </li>
            ))}
          </ol>
        )}
        {sugestoes}
      </div>
    )
  }
  const prosa = resumo.prose
  return (
    <div className="space-y-3">
      {prosa.subject && <p className="font-medium text-gray-100">{prosa.subject}</p>}
      {prosa.text && (
        <p className="max-w-prose leading-relaxed text-gray-300">{prosa.text}</p>
      )}
      {prosa.asks.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-gray-500">Pedidos</p>
          <ol className="space-y-1">
            {prosa.asks.map((pedido, i) => {
              const estado =
                ESTADO_DO_PEDIDO[pedido.status as AskStatus] ?? ESTADO_DO_PEDIDO.unanswered
              return (
                <li key={i} className="text-gray-300">
                  {pedido.text}{' '}
                  <span
                    className={cn('rounded px-1.5 py-0.5 text-xs font-medium', estado.classe)}
                  >
                    {estado.rotulo}
                  </span>
                  {pedido.note && (
                    <span className="ml-2 text-gray-400">{pedido.note}</span>
                  )}
                </li>
              )
            })}
          </ol>
        </div>
      )}
      <ListaDeTexto titulo="Avisos dados ao cliente" itens={prosa.warnings} />
      <ListaDeTexto titulo="O que falta fazer" itens={prosa.todo} />
      <ListaDeTexto
        titulo="Dados levantados"
        itens={prosa.gathered.map((d) => `${d.label}: ${d.value}`)}
      />
      {sugestoes}
    </div>
  )
}

function ResumoDaIA({ grupo, id }: { grupo: AttendanceGroup; id: string }) {
  const [atual, ...anteriores] = grupo.summaries
  return (
    <section aria-labelledby={id} className="min-w-0 space-y-3 break-words text-sm">
      <h3 id={id} className="text-sm font-semibold text-gray-200">
        Resumo gerado pela IA
      </h3>
      {atual ? (
        <CorpoDoResumo resumo={atual} />
      ) : grupo.state === 'in_progress' ? (
        <p className="text-gray-400">
          A conversa ainda está em curso; o resumo nasce no encerramento ou no
          transbordo.
        </p>
      ) : (
        <p className="text-gray-400">Sem resumo para este atendimento.</p>
      )}
      {anteriores.length > 0 && (
        <Collapsible className="border-t border-gray-800/50 pt-2">
          <CollapsibleTrigger className="group inline-flex min-h-11 items-center gap-1 text-xs text-gray-400 hover:text-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-400 sm:min-h-0">
            <ChevronRight aria-hidden="true" className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90 motion-reduce:transition-none" />
            Resumos anteriores ({anteriores.length})
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-4">
            {anteriores.map((resumo, i) => (
              <div key={resumo.id ?? i} className="space-y-2">
                <p className="text-xs text-gray-500">
                  {resumo.kind === 'handoff' ? 'No transbordo' : 'No encerramento'},{' '}
                  <span className="tabular-nums">{quando(resumo.occurred_at)}</span>
                </p>
                <CorpoDoResumo resumo={resumo} />
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  )
}

function contagemDeTurnos(grupo: AttendanceGroup): string {
  const vistos = grupo.executions.length
  return vistos === grupo.executions_total
    ? String(grupo.executions_total)
    : `${vistos} de ${grupo.executions_total} turnos`
}

const COLUNAS_DO_GRUPO = 6

function LinhaDoAtendimento({
  grupo,
  parados,
}: {
  grupo: AttendanceGroup
  parados: Set<string>
}) {
  const [aberto, setAberto] = useState(false)
  const parada = grupo.executions.some((t) => parados.has(t.id))
  const fim = desfecho(grupo)
  const quem = quemFalou(grupo)
  const base = `grupo-${grupo.key.replace(/[^a-zA-Z0-9]/g, '')}`
  return (
    <Collapsible asChild open={aberto} onOpenChange={setAberto}>
      <tbody className="border-t border-gray-800/50 first:border-t-0">
        <tr className={cn('text-gray-300', aberto && 'bg-white/[0.03]')}>
          <td className="whitespace-nowrap px-2 py-0 sm:px-4">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                aria-label={`${quando(grupo.started_at)}, expandir o atendimento de ${quem ?? 'contato não identificado'}`}
                className="group inline-flex min-h-11 w-full items-center gap-2 rounded text-left text-gray-400 hover:text-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-400"
              >
                <ChevronRight aria-hidden="true" className="h-4 w-4 flex-shrink-0 transition-transform group-data-[state=open]:rotate-90 motion-reduce:transition-none" />
                <span className="tabular-nums">{quando(grupo.started_at)}</span>
              </button>
            </CollapsibleTrigger>
          </td>
          <td className="px-2 py-2 sm:px-4">
            <span className="inline-flex flex-wrap items-center gap-1.5 lg:flex-nowrap">
              <span
                className={cn(
                  'rounded-lg px-2 py-0.5 text-xs font-medium sm:whitespace-nowrap sm:rounded-full',
                  fim.classe
                )}
              >
                {fim.rotulo}
              </span>
              {parada && (
                <span
                  className={cn(
                    'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
                    APARENCIA.parada.classe
                  )}
                >
                  {APARENCIA.parada.rotulo}
                </span>
              )}
              {grupo.not_fulfilled && (
                <span className="whitespace-nowrap rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-300">
                  Não atendido
                </span>
              )}
            </span>
            {/* NO CELULAR O CLIENTE MORA AQUI, embaixo do desfecho: a coluna
                própria não cabe em 320 px sem a página rolar de lado. */}
            <span className="mt-1 block max-w-[8.5rem] truncate text-gray-300 sm:hidden">
              {quem ?? <span className="text-gray-500">não identificado</span>}
            </span>
          </td>
          <td className="hidden max-w-[18rem] truncate px-4 py-2 sm:table-cell">
            {quem ?? <span className="text-gray-500">não identificado</span>}
          </td>
          <td className="hidden whitespace-nowrap px-4 py-2 text-right tabular-nums xl:table-cell">
            {contagemDeTurnos(grupo)}
          </td>
          <td className="hidden whitespace-nowrap px-4 py-2 text-right tabular-nums text-gray-400 xl:table-cell">
            {duracaoDoAtendimento(grupo.duration_ms)}
          </td>
          <td className="hidden whitespace-nowrap px-4 py-2 text-right tabular-nums text-gray-400 xl:table-cell">
            {dinheiro(grupo.cost)}
          </td>
        </tr>
        <CollapsibleContent asChild>
          <tr className="bg-white/[0.03]">
            <td colSpan={COLUNAS_DO_GRUPO} className="px-3 pb-5 pt-2 sm:px-6">
              <div className="grid gap-6 xl:grid-cols-2 [&>section+section]:border-t [&>section+section]:border-gray-800/50 [&>section+section]:pt-6 xl:[&>section+section]:border-t-0 xl:[&>section+section]:pt-0">
                <FatosDoAtendimento grupo={grupo} id={`${base}-fatos`} />
                <ResumoDaIA grupo={grupo} id={`${base}-ia`} />
              </div>
            </td>
          </tr>
        </CollapsibleContent>
      </tbody>
    </Collapsible>
  )
}

const ESTADO_DO_TURNO: Record<string, string> = {
  completed: 'concluída',
  success: 'concluída',
  failed: 'falhou',
  running: 'em curso',
  pending: 'na fila',
  cancelled: 'cancelada',
}

/** As execuções que não vieram de conversa: API, chat, e o que antecede o carimbo. */
function SemConversa({ grupos }: { grupos: AttendanceGroup[] }) {
  const turnos = grupos.flatMap((g) => g.executions)
  if (turnos.length === 0) return null
  return (
    <section aria-labelledby="sem-conversa" className="space-y-2">
      <h2 id="sem-conversa" className="text-sm font-semibold text-gray-300">
        Sem conversa
      </h2>
      <p className="text-xs text-gray-500">
        Execuções de API, do chat, ou anteriores a 19/09, quando a execução passou
        a registrar de qual conversa é.
      </p>
      <div className="overflow-x-auto rounded-xl border border-gray-800/50">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="whitespace-nowrap px-4 py-3">Execução</th>
              <th className="w-full px-4 py-3">Agente</th>
              <th className="whitespace-nowrap px-4 py-3">Estado</th>
              <th className="hidden whitespace-nowrap px-4 py-3 sm:table-cell">Início</th>
              <th className="hidden whitespace-nowrap px-4 py-3 text-right lg:table-cell">Custo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/50">
            {turnos.map((turno) => (
              <tr key={turno.id} className="text-gray-300">
                <td className="px-4 py-3 font-mono text-xs">
                  <Link
                    to={`/executions/${turno.id}`}
                    className="text-indigo-300 underline-offset-2 hover:underline"
                  >
                    {turno.id.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-4 py-3 text-gray-200">{turno.agent_name}</td>
                <td className="whitespace-nowrap px-4 py-3 text-gray-400">
                  {ESTADO_DO_TURNO[turno.status] ?? turno.status}
                </td>
                <td className="hidden whitespace-nowrap px-4 py-3 tabular-nums text-gray-400 sm:table-cell">
                  {quando(turno.created_at)}
                </td>
                <td className="hidden whitespace-nowrap px-4 py-3 text-right tabular-nums text-gray-400 lg:table-cell">
                  {dinheiro(turno.cost)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function ExecutionsPage() {
  const { currentOrganization } = useOrganizationStore()
  // O ID, E NÃO O OBJETO, é o que entra nas dependências das buscas — ver a
  // nota em `buscar`. Texto não oscila entre renders.
  const organizacaoId = currentOrganization?.id
  const [execucoes, setExecucoes] = useState<OrganizationExecution[]>([])
  // Spec 128: na unidade "conversas", os ATENDIMENTOS da rota agrupada.
  const [grupos, setGrupos] = useState<AttendanceGroup[]>([])
  // As execuções `running` lidas junto dos grupos, só para o aviso "parada?":
  // o grupo não traz os instantes de que a regra precisa.
  const [rodando, setRodando] = useState<OrganizationExecution[]>([])
  // O aviso é do escritório que o leu: trocar de organização o apaga antes
  // da primeira leitura da nova.
  useEffect(() => setRodando([]), [organizacaoId])
  const [filtro, setFiltro] =
    useState<(typeof FILTROS)[number]['valor']>('todas')
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [cancelando, setCancelando] = useState<string | null>(null)
  // OS FILTROS DO FUNIL (spec 117), lidos da URL para que a barra de vazamento
  // possa linkar para cá já filtrada — e para que a pessoa possa mandar o link.
  const [params, setParams] = useSearchParams()
  const etapa = params.get('etapa')
  const motivo = params.get('motivo')
  const canal = params.get('canal')
  const objetivo = params.get('objetivo')
  // A MESMA JANELA DA BARRA. Sem ela a lista conta o período inteiro e o
  // número não fecha com o agregado que originou o recorte.
  const dias = params.get('dias')
  const unidadePedida = params.get('contando')
  const [contagens, setContagens] = useState<{
    conversas: number | null
    turnos: number | null
  }>({ conversas: null, turnos: null })
  // AS OPÇÕES DOS FILTROS VÊM DO ESCRITÓRIO, nunca de lista no código: os
  // nomes de etapa e de motivo são do documento dele, e fixá-los aqui seria
  // vocabulário de setor no produto (regra de ouro 1).
  // ⚠️ `null` É "AINDA NÃO LI", e é diferente de "não tem".
  //
  // Começando em `[]`, a tela afirmava por alguns segundos que o escritório
  // não tem funil: desenhava a tabela genérica contando turnos e, quando a
  // cascata chegava, trocava tudo debaixo do cursor. O pisca não era estética
  // — era a tela dando uma resposta antes de ter a pergunta respondida.
  const [etapasDisponiveis, setEtapasDisponiveis] = useState<
    { indice: number; nome: string | null }[] | null
  >(null)
  const [motivosDisponiveis, setMotivosDisponiveis] = useState<string[]>([])
  // Os canais que este escritório de fato usou, lidos junto da cascata.
  const [canaisDisponiveis, setCanaisDisponiveis] = useState<string[]>([])
  const transbordo = params.get('transbordo')
  // O RECORTE POR PERGUNTA vem do ranking de "não soube responder": quais
  // conversas fizeram ESTA pergunta. Endereçada pelo hash, nunca pelo texto.
  const pergunta = params.get('pergunta')

  // A UNIDADE ACOMPANHA A TABELA. Quando o escritório tem funil, a tabela é
  // de CONVERSAS — e listar turnos ali faz cada turno repetir a etapa e o
  // motivo da conversa: um "Oi" aparece dizendo "entregou" porque a conversa
  // entregou noutro turno. Foi o que se viu na tela em 20/09.
  // A ESCOLHA É DA PESSOA, E ELA FICA.
  //
  // Ordem de precedência, e cada degrau tem um motivo:
  //   1. a URL — é o que a barra de vazamento manda no link, e o que a pessoa
  //      compartilha; um link tem de abrir contando o que ele diz;
  //   2. o que ela escolheu da última vez, lido do navegador SINCRONAMENTE na
  //      primeira renderização, senão volta o pisca que isto corrige;
  //   3. o padrão, que segue a tabela: escritório com funil lista conversas.
  const lembrada = unidadeLembrada(organizacaoId)
  const contando: 'turnos' | 'conversas' =
    unidadePedida === 'turnos' || unidadePedida === 'conversas'
      ? unidadePedida
      : (lembrada ??
        (etapasDisponiveis === null
          ? 'conversas'
          : etapasDisponiveis.length > 0
            ? 'conversas'
            : 'turnos'))
  // A PÁGINA VIAJA NA URL, como o recorte: voltar do detalhe cai onde estava,
  // e o link pode ser mandado apontando para a página certa.
  const pagina = Math.max(0, Number(params.get('pagina') ?? 0) || 0)
  const [total, setTotal] = useState(0)

  // Qual filtro tem requisição em voo — `null` quando não há nenhuma.
  const emVoo = useRef<string | null>(null)

  // As opções são lidas uma vez por organização e janela — não a cada tique.
  useEffect(() => {
    if (!organizacaoId) return
    const janela = { dias: dias ? Number(dias) : 30 }
    void (async () => {
      const [c, m] = await Promise.allSettled([
        desempenhoService.cascata(organizacaoId, janela),
        desempenhoService.motivos(organizacaoId, janela),
      ])
      if (c.status === 'fulfilled') {
        setEtapasDisponiveis(
          c.value.etapas.map((e) => ({ indice: e.indice, nome: e.nome }))
        )
        // O FRONTEND E O BACKEND SOBEM SEPARADOS. Na janela entre um deploy
        // e outro o campo não vem, e um `.map` sobre indefinido derruba a
        // tela inteira por causa de um filtro.
        setCanaisDisponiveis(c.value.canais ?? [])
      } else {
        // FALHA NÃO É "NÃO TEM FUNIL". Deixar em `null` travaria a tela para
        // sempre; assumir `[]` afirmaria sobre o escritório uma coisa que não
        // se leu. Fica a lista vazia com o registro de que a leitura falhou —
        // a tabela genérica aparece, que é o comportamento anterior a esta
        // feature, e nada é afirmado sobre o funil.
        setEtapasDisponiveis([])
      }
      if (m.status === 'fulfilled') {
        setMotivosDisponiveis([...new Set(m.value.map((x) => x.motivo))])
      }
    })()
  }, [organizacaoId, dias])

  const trocarFiltro = (chave: string, valor: string | null) => {
    const proximo = new URLSearchParams(params)
    if (valor === null || valor === '') proximo.delete(chave)
    else proximo.set(chave, valor)
    // MUDAR O RECORTE VOLTA PARA A PRIMEIRA PÁGINA. Sem isto, filtrar estando
    // na página 4 deixaria a lista vazia com um recorte que tem resultado — e
    // a pessoa concluiria que o filtro não achou nada.
    if (chave !== 'pagina') proximo.delete('pagina')
    setParams(proximo, { replace: true })
  }

  // HÁ RECORTE quando qualquer filtro do funil está aplicado.
  const recortado = Boolean(
    etapa || motivo || canal || objetivo || transbordo || pergunta
  )

  // AS COLUNAS DO FUNIL SEGUEM O ESCRITÓRIO, e não a página.
  //
  // Duas versões erradas antes desta. A primeira as prendia ao recorte: quem
  // navegasse a lista inteira nunca as via. A segunda as prendia ao carimbo
  // presente NA PÁGINA — e como só execução posterior a 19/09 tem carimbo, a
  // coluna sumia inteira e a funcionalidade parecia não existir.
  //
  // A condição certa é a mesma da barra de filtros: se dá para filtrar por
  // etapa, dá para ver a etapa. Linha sem carimbo mostra um traço, e o rodapé
  // diz quantas são e por quê — ausência explicada é informação; coluna que
  // some é a tela escondendo o próprio estado.
  // ENQUANTO NÃO SE SABE, NÃO SE AFIRMA. `null` não desenha tabela nenhuma —
  // é o que elimina a troca de tabela debaixo do cursor.
  const sabemosDoFunil = etapasDisponiveis !== null
  const temFunil = (etapasDisponiveis?.length ?? 0) > 0
  const semCarimbo = execucoes.filter((e) => e.channel_session_id === null).length

  const limparFiltro = (chave: string) => {
    const proximo = new URLSearchParams(params)
    proximo.delete(chave)
    proximo.delete('pagina')
    setParams(proximo, { replace: true })
  }

  const trocarUnidade = (unidade: 'turnos' | 'conversas') => {
    lembrarUnidade(organizacaoId, unidade)
    const proximo = new URLSearchParams(params)
    proximo.set('contando', unidade)
    setParams(proximo, { replace: true })
  }

  const buscar = useCallback(async () => {
    if (!organizacaoId) return
    // UMA REQUISIÇÃO POR VEZ. Sem esta guarda, uma resposta lenta se sobrepõe ao
    // próximo tique de 5 s e a última a chegar ganha — que pode ser a mais
    // velha. E uma aba aberta faria 12 requisições por minuto contra um pool de
    // duas conexões por instância.
    // O CLIQUE NO FILTRO NÃO PODE SER ENGOLIDO. A guarda existe para o tique
    // automático de 5 s; quando o filtro muda, a requisição em voo deixou de
    // interessar e a nova tem de sair mesmo assim.
    // A CHAVE É O RECORTE INTEIRO, não só a aba de status.
    //
    // Com a chave só no `filtro`, tirar um chip enquanto havia requisição em
    // voo era engolido pela guarda — e quando a resposta velha chegava, ela
    // escrevia linhas e contagens de um recorte que já não existia, sob os
    // chips do novo. Um subconjunto filtrado apresentado como a lista inteira.
    // E o escritório entra na chave: trocar de organização com uma busca em voo
    // não pode deixar a resposta do escritório anterior escrever sobre o novo.
    const chave = `${organizacaoId}|${contando}|${filtro}|${etapa}|${motivo}|${canal}|${objetivo}|${dias}|${transbordo}|${pergunta}|${pagina}`
    if (emVoo.current && emVoo.current === chave) return
    emVoo.current = chave
    const escolhido = FILTROS.find((f) => f.valor === filtro)
    // "EM CURSO" MUDA DE SENTIDO COM A UNIDADE — ver a nota em `FILTROS`.
    // Numa lista de conversas, `status=running` nunca devolveria nada.
    const porConversaAberta = filtro === 'em_curso' && contando === 'conversas'
    // Os filtros são os MESMOS nas duas rotas: o backend monta as duas pela
    // mesma função, e o grupo nunca discorda da lista de turnos.
    const filtros = {
      status: porConversaAberta ? undefined : escolhido?.status,
      conversaAberta: porConversaAberta ? true : undefined,
      // `?etapa=abc` viraria `NaN` e 422 com banner genérico. Número
      // inválido é o mesmo que filtro nenhum.
      etapaAlcancada:
        etapa && Number.isFinite(Number(etapa)) ? Number(etapa) : undefined,
      motivo: motivo ?? undefined,
      canal: canal ?? undefined,
      objetivo: objetivo ?? undefined,
      comTransbordo:
        transbordo === 'sim' ? true : transbordo === 'nao' ? false : undefined,
      desdeDias:
        dias && Number.isFinite(Number(dias)) ? Number(dias) : undefined,
      pergunta: pergunta ?? undefined,
    }
    try {
      if (contando === 'conversas') {
        // A UNIDADE "CONVERSAS" É O ATENDIMENTO (spec 128): uma linha por
        // sessão e ciclo, paginada por grupos. A chave `contando` da URL é
        // quem escolhe a rota; ela não vai ao backend.
        const [resposta, emAndamento] = await Promise.all([
          execucoesService.listarAtendimentos(organizacaoId, filtros, {
            limit: POR_PAGINA,
            offset: pagina * POR_PAGINA,
          }),
          // O SINAL DE PARADA NÃO SOME NA VISTA AGRUPADA. Falhar esta leitura
          // não derruba a lista, e apaga o aviso: repetir o último valor lido
          // afirmaria uma parada que já não se sabe se existe.
          rodandoMaisAntigas(organizacaoId).catch(() => []),
        ])
        if (emVoo.current !== chave) return
        setRodando(emAndamento)
        // A LISTA INTEIRA É SUBSTITUÍDA, e as linhas são chaveadas pela chave
        // do grupo: a conversa em curso que encerrou entre dois tiques vira a
        // mesma linha encerrada, sem duplicar e sem perder o bloco aberto.
        setGrupos(resposta.items)
        // O alternador mostra CONVERSAS, o mesmo número da unidade "turnos"; a
        // paginação segue os grupos (um por ciclo, mais cada execução sem conversa).
        setContagens({
          conversas: resposta.total_conversations,
          turnos: resposta.total_turns,
        })
        setTotal(resposta.total_groups)
        setErro(null)
        return
      }
      const resposta = await execucoesService.listar(organizacaoId, {
        ...filtros,
        limit: POR_PAGINA,
        offset: pagina * POR_PAGINA,
      })
      // RESPOSTA DE RECORTE QUE JÁ NÃO É O CORRENTE NÃO ESCREVE NADA. Sem
      // isto, a última a chegar ganha — e pode ser a mais velha.
      if (emVoo.current !== chave) return
      setExecucoes(resposta.items)
      setContagens({
        conversas: resposta.total_de_conversas,
        turnos: resposta.total_de_turnos ?? resposta.total,
      })
      setTotal(resposta.total)
      setErro(null)
    } catch {
      setErro('Não foi possível ler as execuções agora.')
    } finally {
      if (emVoo.current === chave) emVoo.current = null
      setCarregando(false)
    }
    // ⚠️ O ESCRITÓRIO ENTRA AQUI PELO ID, E NÃO PELO OBJETO.
    //
    // Com o objeto, qualquer fonte que devolva uma referência nova a cada
    // render — e basta um `useStore()` sem seletor — refaz `buscar`, o efeito
    // dispara, o estado muda, e o render seguinte refaz `buscar` de novo:
    // requisição contínua em vez do tique de 5 s. O `id` é texto e não tem
    // como oscilar.
    //
    // Nenhum teste desta tela clicava em nada até 20/09, e foi o primeiro
    // clique que revelou o laço.
  }, [organizacaoId, filtro, etapa, motivo, canal, objetivo, dias, transbordo, pergunta, contando, pagina])

  useEffect(() => {
    setCarregando(true)
    void buscar()
  }, [buscar])

  useEffect(() => {
    // A TELA SE ATUALIZA SOZINHA porque o que ela mostra muda sem o usuário
    // fazer nada. Cinco segundos é o intervalo do desenho aprovado.
    const relogio = setInterval(() => void buscar(), RECARGA_MS)
    return () => clearInterval(relogio)
  }, [buscar])

  const cancelar = async (execucao: OrganizationExecution) => {
    setCancelando(execucao.id)
    try {
      await execucoesService.cancelar(execucao.id)
      toast.success('Cancelamento pedido.')
      await buscar()
    } catch {
      toast.error('Não foi possível cancelar esta execução.')
    } finally {
      setCancelando(null)
    }
  }

  if (!currentOrganization) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Activity className="mb-4 h-10 w-10 text-amber-400" />
        <p className="text-gray-400">Escolha uma organização para ver as execuções.</p>
      </div>
    )
  }

  const paradas = (contando === 'conversas' ? rodando : execucoes).filter(
    (e) => estadoDaExecucao(e) === 'parada'
  )
  const idsParados = new Set(paradas.map((e) => e.id))

  return (
    <div className="space-y-6">
      {/* Sem título repetido: o cabeçalho da aplicação já diz "Execuções". */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <FaixaDePeriodo
            opcoes={FILTROS}
            valor={filtro}
            aoEscolher={setFiltro}
            rotulo="Filtro de execuções"
          />
          {/* O PERÍODO, que faltava: sem ele a lista traz tudo desde
              sempre e o número nunca fecha com a barra do funil, que é
              sempre de uma janela. */}
          <FaixaDePeriodo
            opcoes={PERIODOS}
            valor={dias ? Number(dias) : 30}
            aoEscolher={(v) => trocarFiltro('dias', String(v))}
            rotulo="Período"
          />
          <p className="text-sm text-gray-500">
            Atualiza sozinha a cada {RECARGA_MS / 1000} s.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EscopoDaOrganizacao nome={currentOrganization.name} />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Atualizar"
            onClick={() => void buscar()}
          >
            <RefreshCw className={cn('h-4 w-4', carregando && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {paradas.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" />
          <p className="text-sm text-amber-200/90">
            <strong>
              {paradas.length}{' '}
              {paradas.length === 1 ? 'execução parada?' : 'execuções paradas?'}
            </strong>{' '}
            Começaram e não dão sinal há mais de {TETO_DA_FILA_MIN} minutos — mais
            do que o teto que a fila dá para a entrega. O que acontece daqui em
            diante depende de por onde ela parou: se o serviço devolveu a execução
            à fila, a próxima entrega a retoma; se o processo morreu sem devolver,
            a varredura a encerra com motivo. Em nenhum dos dois casos ela
            continua consumindo modelo.
          </p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* A BARRA DE FILTROS DO FUNIL — sempre disponível.                    */}
      {/*                                                                     */}
      {/* A primeira versão só mostrava o recorte quando se CHEGAVA por um    */}
      {/* link da barra de vazamento: não havia como aplicar um filtro a      */}
      {/* partir daqui, e `transbordo` era aceito pelo backend e nunca        */}
      {/* setado por ninguém. Filtro que existe e não tem como ser usado é    */}
      {/* mecanismo sem chamador, um andar acima.                             */}
      {/*                                                                     */}
      {/* As opções vêm do documento do escritório — nenhum nome de etapa ou  */}
      {/* de motivo está escrito aqui.                                        */}
      {/* ------------------------------------------------------------------ */}
      {temFunil && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-800/50 bg-[#12121a] p-3">
          <span className="text-xs uppercase tracking-wide text-gray-500">
            filtrar pelo funil
          </span>

          <label className="sr-only" htmlFor="f-etapa">
            Parou na etapa
          </label>
          <select
            id="f-etapa"
            value={etapa ?? ''}
            onChange={(e) => trocarFiltro('etapa', e.target.value || null)}
            className="rounded-md border border-gray-700/50 bg-white/5 px-2 py-1 text-xs text-gray-300"
          >
            <option value="">parou em: qualquer etapa</option>
            {(etapasDisponiveis ?? []).map((e) => (
              <option key={e.indice} value={e.indice}>
                parou em {e.nome ?? `etapa ${e.indice}`}
              </option>
            ))}
          </select>

          <label className="sr-only" htmlFor="f-motivo">
            Motivo
          </label>
          <select
            id="f-motivo"
            value={motivo ?? ''}
            onChange={(e) => trocarFiltro('motivo', e.target.value || null)}
            className="rounded-md border border-gray-700/50 bg-white/5 px-2 py-1 text-xs text-gray-300"
            disabled={motivosDisponiveis.length === 0}
          >
            <option value="">motivo: qualquer</option>
            {motivosDisponiveis.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <label className="sr-only" htmlFor="f-canal">
            Canal
          </label>
          <select
            id="f-canal"
            value={canal ?? ''}
            onChange={(e) => trocarFiltro('canal', e.target.value || null)}
            className="rounded-md border border-gray-700/50 bg-white/5 px-2 py-1 text-xs text-gray-300"
          >
            <option value="">canal: qualquer</option>
            {canaisDisponiveis.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <label className="sr-only" htmlFor="f-transbordo">
            Transbordo
          </label>
          <select
            id="f-transbordo"
            value={transbordo ?? ''}
            onChange={(e) => trocarFiltro('transbordo', e.target.value || null)}
            className="rounded-md border border-gray-700/50 bg-white/5 px-2 py-1 text-xs text-gray-300"
          >
            <option value="">transbordo: qualquer</option>
            <option value="sim">com transbordo</option>
            <option value="nao">sem transbordo</option>
          </select>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* O RECORTE DO FUNIL, e as DUAS contagens (spec 117)                  */}
      {/*                                                                     */}
      {/* A UNIDADE NÃO DEPENDE DE HAVER RECORTE, e a primeira versão fazia  */}
      {/* depender: tudo morava num bloco preso a `recortado`, então para     */}
      {/* trocar de conversas para turnos era preciso aplicar um filtro       */}
      {/* qualquer e desfazer depois.                                         */}
      {/*                                                                     */}
      {/* O que depende do recorte são os CHIPS — sem filtro não há o que     */}
      {/* listar ali. A escolha da unidade e as duas contagens valem em       */}
      {/* qualquer lista de um escritório com funil.                          */}
      {/* ------------------------------------------------------------------ */}
      {(recortado || temFunil) && (
        <div className="space-y-3 rounded-xl border border-indigo-500/25 bg-[#12121a] p-4">
          {recortado && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs uppercase tracking-wide text-gray-500">
                recorte
              </span>
              {etapa && (
                <Chip
                  rotulo={`parou na etapa ${etapa}`}
                  aoLimpar={() => limparFiltro('etapa')}
                />
              )}
              {motivo && (
                <Chip rotulo={motivo} aoLimpar={() => limparFiltro('motivo')} />
              )}
              {canal && (
                <Chip rotulo={canal} aoLimpar={() => limparFiltro('canal')} />
              )}
              {objetivo && (
                <Chip rotulo={objetivo} aoLimpar={() => limparFiltro('objetivo')} />
              )}
              {dias && (
                <Chip
                  rotulo={`últimos ${dias} dias`}
                  aoLimpar={() => limparFiltro('dias')}
                />
              )}
              {transbordo && (
                <Chip
                  rotulo={transbordo === 'sim' ? 'com transbordo' : 'sem transbordo'}
                  aoLimpar={() => limparFiltro('transbordo')}
                />
              )}
              {/* O CHIP DIZ O QUE É, e não mostra o hash: `pergunta a1b2c3…`
                  não significa nada para quem lê. O que importa é que o
                  recorte existe e dá para desfazer. */}
              {pergunta && (
                <Chip
                  rotulo="pergunta sem resposta"
                  aoLimpar={() => limparFiltro('pergunta')}
                />
              )}
            </div>
          )}

          <div
            className={cn(
              'flex flex-wrap items-center gap-4',
              recortado && 'border-t border-gray-800/50 pt-3'
            )}
          >
            <span className="text-xs uppercase tracking-wide text-gray-500">
              contando
            </span>
            {(['conversas', 'turnos'] as const).map((unidade) => (
              <button
                key={unidade}
                type="button"
                onClick={() => trocarUnidade(unidade)}
                className={cn(
                  'rounded-md border px-3 py-1 text-xs',
                  contando === unidade
                    ? 'border-emerald-500/35 bg-emerald-500/10 text-emerald-400'
                    : 'border-gray-700/50 bg-white/5 text-gray-400'
                )}
              >
                {unidade}
              </button>
            ))}
            <span className="flex-grow" />
            {/* OS DOIS NÚMEROS LADO A LADO, e é deliberado. A mesma queda é 33
                no funil e 140 aqui; um número sem o outro faz quem lê concluir
                que o relatório está errado. */}
            <span className="text-sm text-gray-200">
              <strong className="font-semibold tabular-nums">
                {contagens.conversas ?? '—'}
              </strong>{' '}
              conversas{' '}
              <span className="text-gray-500">
                · {contagens.turnos ?? '—'} turnos
              </span>
            </span>
          </div>
        </div>
      )}

      {erro && <p className="text-sm text-red-400">{erro}</p>}

      <div className="overflow-x-auto rounded-xl border border-gray-800/50">
        {!sabemosDoFunil ? (
          /* ---------------------------------------------------------------- */
          /* ENQUANTO NÃO SE SABE, NÃO SE AFIRMA.                              */
          /*                                                                   */
          /* Antes daqui a tela desenhava a tabela genérica por padrão e a     */
          /* trocava pela do funil quando a cascata chegava — alguns segundos  */
          /* mostrando a resposta errada, e a lista inteira saltando debaixo   */
          /* do cursor. Não era lentidão: era a tela responder antes de ter a  */
          /* resposta.                                                         */
          /*                                                                   */
          /* Qual tabela desenhar depende de o escritório ter funil, e isso é  */
          /* uma leitura. Esperar por ela custa um instante de espera honesta  */
          /* no lugar de um instante de afirmação falsa.                       */
          /* ---------------------------------------------------------------- */
          <p className="px-4 py-10 text-center text-sm text-gray-500">
            Lendo o funil deste escritório…
          </p>
        ) : contando === 'conversas' ? (
          /* ---------------------------------------------------------------- */
          /* A LISTA POR ATENDIMENTO (spec 128). Substitui a tabela de        */
          /* conversas da spec 117, que mostrava o turno mais recente de cada */
          /* conversa: agora a linha É o atendimento, com o bloco dele.       */
          /* Cada grupo é um `<tbody>`: várias numa tabela é HTML válido, e é */
          /* o que deixa o `Collapsible` envolver a linha e o bloco juntos.   */
          /* ---------------------------------------------------------------- */
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-2 py-3 sm:px-4">Quando</th>
                <th className="w-full whitespace-nowrap px-2 py-3 sm:w-auto sm:px-4">
                  Conversa
                </th>
                <th className="hidden w-full px-4 py-3 sm:table-cell">Cliente</th>
                <th className="hidden whitespace-nowrap px-4 py-3 text-right xl:table-cell">
                  Turnos
                </th>
                <th className="hidden whitespace-nowrap px-4 py-3 text-right xl:table-cell">
                  Duração
                </th>
                <th className="hidden whitespace-nowrap px-4 py-3 text-right xl:table-cell">
                  Custo
                </th>
              </tr>
            </thead>
            {grupos
              .filter((g) => g.state !== 'orphan')
              .map((g) => (
                <LinhaDoAtendimento key={g.key} grupo={g} parados={idsParados} />
              ))}
            {!carregando && grupos.filter((g) => g.state !== 'orphan').length === 0 && (
              <tbody>
                <tr>
                  <td
                    colSpan={COLUNAS_DO_GRUPO}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    Nenhuma conversa neste recorte.
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        ) : (
<table className="w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="whitespace-nowrap px-4 py-3">Execução</th>
              {/* A FOLGA DA TABELA MORA AQUI. Sem um `w-full` numa coluna, o
                  navegador distribuía a sobra na última coluna automática — e
                  aparecia um vão morto de uns 300 px entre Custo e Ação. */}
              <th className="w-full px-4 py-3">Agente</th>
              <th className="whitespace-nowrap px-4 py-3">Estado</th>
              <th className="whitespace-nowrap px-4 py-3">Início</th>
              <th className="whitespace-nowrap px-4 py-3">Duração</th>
              {/* AS COLUNAS DO FUNIL só aparecem no recorte do funil: fora
                  dele a maioria das execuções não vem do atendimento, e a
                  coluna seria um traço em toda linha. */}
              {temFunil && (
                <>
                  <th className="whitespace-nowrap px-4 py-3">Parou em</th>
                  <th className="whitespace-nowrap px-4 py-3">Motivo</th>
                </>
              )}
              <th className="whitespace-nowrap px-4 py-3">Custo</th>
              {/* A COLUNA DE AÇÃO GANHA NOME E LARGURA. Sem cabeçalho ela ficava
                  órfã na direita, e sem largura mínima o botão saltava de lugar
                  conforme a linha tivesse ou não ação. */}
              <th className="w-32 px-4 py-3 text-right">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/50">
            {execucoes.map((execucao) => {
              const estado = estadoDaExecucao(execucao)
              const aparencia = APARENCIA[estado]
              const cancelavel = estado === 'em_curso' || estado === 'parada'
              return (
                <tr key={execucao.id} className="text-gray-300">
                  <td className="px-4 py-3 font-mono text-xs">
                    {/* CADA LINHA ABRE. É o destino de todo link do funil, e
                        é o que faz o número levar a algum lugar. */}
                    <Link
                      to={`/executions/${execucao.id}`}
                      className="text-indigo-300 underline-offset-2 hover:underline"
                    >
                      {execucao.id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-200">{execucao.agent_name}</p>
                    <p className="text-xs text-gray-500">
                      {execucao.project_name}
                      {execucao.texto_expirado && (
                        // A LINHA PERMANECE DEPOIS DO EXPURGO, marcada. A purga
                        // nunca faz uma conversa sumir de um número: o custo e
                        // o caminho no funil continuam contando.
                        <span className="ml-2 text-amber-500/80">
                          texto expirado
                        </span>
                      )}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        aparencia.classe
                      )}
                      title={
                        estado === 'parada'
                          ? `sem sinal há ${Math.round(minutosSemSinal(execucao))} min`
                          : undefined
                      }
                    >
                      {aparencia.rotulo}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-400">
                    {hora(execucao.started_at ?? execucao.created_at)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-400">
                    {duracao(execucao)}
                  </td>
                  {temFunil && (
                    <>
                      <td className="px-4 py-3 text-gray-300">
                        {execucao.etapa_alcancada ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        {execucao.motivo ? (
                          <span className="text-gray-300">
                            {execucao.motivo}{' '}
                            {/* A FONTE DO MOTIVO, sempre visível: sem ela,
                                palpite vira medição. */}
                            <span className="text-xs text-gray-500">
                              ({execucao.fonte_do_motivo ?? 'sem fonte'})
                            </span>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 tabular-nums text-gray-400">
                    {dinheiro(execucao.cost)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {cancelavel && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={cancelando === execucao.id}
                        onClick={() => void cancelar(execucao)}
                      >
                        Cancelar
                      </Button>
                    )}
                    {estado === 'falhou' && execucao.error_message && (
                      <span
                        className="cursor-help text-xs text-red-400"
                        title={execucao.error_message}
                      >
                        ver motivo
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
            {!carregando && execucoes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  Nenhuma execução neste filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        )}
      </div>

      {sabemosDoFunil && contando === 'conversas' && (
        <SemConversa grupos={grupos.filter((g) => g.state === 'orphan')} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* PAGINAÇÃO                                                           */}
      {/*                                                                     */}
      {/* A lista trazia 50 linhas e parava ali, sem dizer que havia mais —   */}
      {/* quem procurasse uma conversa fora das 50 concluiria que ela não     */}
      {/* existe. O número total vem do endpoint, e a página viaja na URL.    */}
      {/* ------------------------------------------------------------------ */}
      {/* A AUSÊNCIA EXPLICADA. Execução anterior ao carimbo não tem como
          apontar para a conversa — e um traço sem explicação faria parecer
          que o funil não mediu, quando o que falta é o elo. */}
      {temFunil && contando === 'turnos' && semCarimbo > 0 && (
        <p className="text-xs leading-relaxed text-gray-500">
          {semCarimbo === execucoes.length
            ? 'Nenhuma execução desta página tem caminho no funil'
            : `${semCarimbo} de ${execucoes.length} execuções desta página não têm caminho no funil`}
          : elas são anteriores a 19/09, quando a execução passou a registrar de
          qual conversa é. O funil delas existe no relatório; o que falta é o
          elo entre a linha e a conversa, e ele não é reconstruível para trás.
        </p>
      )}

      {total > POR_PAGINA && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {pagina * POR_PAGINA + 1}–
            {Math.min((pagina + 1) * POR_PAGINA, total)} de {inteiro(total)}{' '}
            {contando === 'conversas' ? 'atendimentos' : 'execuções'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={pagina === 0}
              onClick={() => trocarFiltro('pagina', String(pagina - 1))}
            >
              Anterior
            </Button>
            <span className="text-sm tabular-nums text-gray-400">
              {pagina + 1} / {Math.ceil(total / POR_PAGINA)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={(pagina + 1) * POR_PAGINA >= total}
              onClick={() => trocarFiltro('pagina', String(pagina + 1))}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
